const editorialMenuCloseTimers = new WeakMap();
const editorialHoverQuery = window.matchMedia('(min-width: 1000px) and (hover: hover) and (pointer: fine)');
const editorialLoadingFallbacks = new WeakMap();

const setEditorialButtonLoading = (button, loading) => {
  if (!button) return;

  const previousFallback = editorialLoadingFallbacks.get(button);
  if (previousFallback) window.clearTimeout(previousFallback);

  if (loading) {
    button.setAttribute('aria-busy', 'true');
    editorialLoadingFallbacks.set(button, window.setTimeout(() => {
      button.removeAttribute('aria-busy');
      editorialLoadingFallbacks.delete(button);
    }, 15000));
  } else {
    button.removeAttribute('aria-busy');
    editorialLoadingFallbacks.delete(button);
  }
};

const startEditorialQuickBuyLoading = (button) => {
  const modalId = button.getAttribute('aria-controls');
  const modal = modalId ? document.getElementById(modalId) : null;
  if (!modal) return;

  setEditorialButtonLoading(button, true);

  const stopLoading = () => setEditorialButtonLoading(button, false);
  modal.addEventListener('dialog:after-show', stopLoading, { once: true });
  modal.addEventListener('dialog:after-hide', stopLoading, { once: true });
};

/* --------------------------------------------------------------------------------------------------------------------
Direct add to cart on product cards: match the selected option to a variant, update the hidden id input,
the add to cart button state and the card price.
-------------------------------------------------------------------------------------------------------------------- */

const formatEditorialMoney = (cents, format) => {
  const template = format || '${{amount}}';
  const amount = (Number(cents || 0) / 100).toFixed(2);
  const [integerPart, decimalPart] = amount.split('.');
  const withCommaSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const withDotSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return template
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withCommaSeparator)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${withCommaSeparator},${decimalPart}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, integerPart)
    .replace(/\{\{\s*amount\s*\}\}/g, `${withCommaSeparator}.${decimalPart}`)
    .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimalPart}`)
    .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, withDotSeparator);
};

const updateCardVariant = (input) => {
  const formWrapper = input.closest('[data-card-form]');
  if (!formWrapper) return;

  const variantsScript = formWrapper.querySelector('script[data-card-variants]');
  const idInput = formWrapper.querySelector('[data-card-variant-id]');
  const button = formWrapper.querySelector('.product-card__buy button[type="submit"]');
  if (!variantsScript || !idInput || !button) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch (error) {
    return;
  }

  const variant = variants.find((item) => Array.isArray(item.options) && item.options[0] === input.value);

  if (!variant) {
    button.disabled = true;
    return;
  }

  idInput.value = variant.id;
  button.disabled = !variant.available;
  button.textContent = variant.available
    ? formWrapper.dataset.addLabel || button.textContent
    : formWrapper.dataset.soldOutLabel || button.textContent;

  const priceList = input.closest('product-card')?.querySelector('.price-list');
  const moneyFormat = window.themeVariables?.settings?.moneyWithCurrencyFormat;

  if (priceList && moneyFormat) {
    const price = `<span class="price">${formatEditorialMoney(variant.price, moneyFormat)}</span>`;
    const compareAt = variant.compare_at_price > variant.price
      ? `<s class="price price--compare">${formatEditorialMoney(variant.compare_at_price, moneyFormat)}</s>`
      : '';
    priceList.innerHTML = `${compareAt}${price}`;
  }
};

document.addEventListener('change', (event) => {
  const input = event.target.closest?.('input[data-card-option], select[data-card-option]');
  if (input) {
    const widthLabel = input.closest('.product-card__size-select-wrapper')?.querySelector('[data-card-option-width]');
    if (widthLabel) widthLabel.textContent = input.selectedOptions[0].textContent.trim();
    updateCardVariant(input);
  }
});

const updateEditorialProductMedia = (gallery, featuredMediaId) => {
  if (gallery.dataset.desktopLayout !== 'editorial_story' && gallery.dataset.mobileLayout !== 'editorial_story') return;

  const allMedia = Array.from(gallery.querySelectorAll('.product-gallery__media'));
  const visibleImages = allMedia.filter((media) => media.dataset.mediaType === 'image' && !media.hidden);
  const featuredMedia = visibleImages.find((media) => media.dataset.mediaId === String(featuredMediaId))
    || visibleImages.find((media) => media.classList.contains('product-gallery__media--editorial-featured'))
    || visibleImages[0];

  allMedia.forEach((media) => {
    const roleClasses = Array.from(media.classList).filter((className) => className.startsWith('product-gallery__media--editorial-'));
    media.classList.remove(...roleClasses);
    media.classList.add('product-gallery__media--editorial-extra');
  });

  if (!featuredMedia) {
    gallery.dataset.editorialMediaCount = '0';
    return;
  }

  featuredMedia.classList.remove('product-gallery__media--editorial-extra');
  featuredMedia.classList.add('product-gallery__media--editorial-featured');

  visibleImages
    .filter((media) => media !== featuredMedia)
    .slice(0, 2)
    .forEach((media, index) => {
      media.classList.remove('product-gallery__media--editorial-extra');
      media.classList.add(`product-gallery__media--editorial-secondary-${index + 1}`);
    });

  gallery.dataset.editorialMediaCount = String(Math.min(visibleImages.length, 3));
};

document.addEventListener('variant:change', (event) => {
  const formId = event.detail?.formId;
  const featuredMediaId = event.detail?.variant?.featured_media?.id;

  if (!formId) return;

  document.querySelectorAll('product-gallery').forEach((gallery) => {
    if (gallery.getAttribute('form') === formId) {
      updateEditorialProductMedia(gallery, featuredMediaId);
    }
  });
}, true);

const clearEditorialMenuCloseTimer = (menu) => {
  const timer = editorialMenuCloseTimers.get(menu);

  if (timer) {
    window.clearTimeout(timer);
    editorialMenuCloseTimers.delete(menu);
  }
};

const closeEditorialHeaderMenus = (except = null) => {
  document.querySelectorAll('[data-editorial-header-menu][open]').forEach((menu) => {
    if (menu !== except) {
      clearEditorialMenuCloseTimer(menu);
      menu.open = false;
    }
  });
};

const getEditorialProductMenuSurface = (target) =>
  target?.closest?.('.editorial-product-menu__toggle, [data-editorial-product-menu] [data-editorial-menu-panel]');

const hydrateEditorialTabPanel = (panel) => {
  const template = panel?.querySelector(':scope > template[data-editorial-panel-template]');
  if (!template) return;

  template.replaceWith(template.content.cloneNode(true));
};

document.addEventListener('pointerover', (event) => {
  if (!editorialHoverQuery.matches) return;

  const surface = getEditorialProductMenuSurface(event.target);
  const menu = surface?.closest('[data-editorial-product-menu]');

  if (!menu || menu.dataset.editorialMenuTrigger !== 'hover') return;

  clearEditorialMenuCloseTimer(menu);
  closeEditorialHeaderMenus(menu);
  menu.open = true;
});

document.addEventListener('pointerout', (event) => {
  if (!editorialHoverQuery.matches) return;

  const surface = getEditorialProductMenuSurface(event.target);
  const menu = surface?.closest('[data-editorial-product-menu]');

  if (!menu || menu.dataset.editorialMenuTrigger !== 'hover') return;

  const nextSurface = getEditorialProductMenuSurface(event.relatedTarget);
  if (nextSurface?.closest('[data-editorial-product-menu]') === menu) return;

  clearEditorialMenuCloseTimer(menu);
  editorialMenuCloseTimers.set(
    menu,
    window.setTimeout(() => {
      menu.open = false;
      editorialMenuCloseTimers.delete(menu);
    }, 240)
  );
});

document.addEventListener('click', (event) => {
  const quickBuyButton = event.target.closest('.product-card__quick-add-button[aria-controls]');
  if (quickBuyButton) startEditorialQuickBuyLoading(quickBuyButton);

  const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
  if (checkoutButton) setEditorialButtonLoading(checkoutButton, true);

  const closeButton = event.target.closest('[data-editorial-menu-close]');
  if (closeButton) {
    const menu = closeButton.closest('[data-editorial-header-menu]');
    if (menu) {
      clearEditorialMenuCloseTimer(menu);
      menu.open = false;
    }
    return;
  }

  if (event.target.closest('.header__secondary-nav a, .editorial-desktop-nav__link')) {
    closeEditorialHeaderMenus();
  }

  const tab = event.target.closest('[data-editorial-tab]');
  if (tab) {
    const tabs = tab.closest('[data-editorial-tabs]');
    const target = tab.getAttribute('aria-controls');
    if (!tabs || !target) return;

    const panels = Array.from(tabs.querySelectorAll('[data-editorial-panel]'));
    const targetPanel = panels.find((panel) => panel.id === target);
    if (!targetPanel) return;

    hydrateEditorialTabPanel(targetPanel);

    tabs.querySelectorAll('[data-editorial-tab]').forEach((item) => {
      item.setAttribute('aria-selected', String(item === tab));
    });

    panels.forEach((panel) => {
      panel.hidden = panel.id !== target;
    });
    return;
  }

  const control = event.target.closest('[data-editorial-scroll]');
  if (control) {
    const root = control.closest('[data-editorial-carousel]');
    const rail = root?.querySelector('[data-editorial-rail]');
    if (!rail) return;

    const direction = control.dataset.editorialScroll === 'previous' ? -1 : 1;
    const distance = Math.max(rail.clientWidth * 0.8, 280);
    rail.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }
});

document.addEventListener('submit', (event) => {
  const submitter = event.submitter;
  if (submitter?.matches?.('button[name="checkout"], input[name="checkout"]')) {
    setEditorialButtonLoading(submitter, true);
  }
}, true);

document.addEventListener('toggle', (event) => {
  const menu = event.target.closest?.('[data-editorial-header-menu]');
  if (menu?.open) closeEditorialHeaderMenus(menu);
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeEditorialHeaderMenus();
});
